import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatError, resolveImage, uploadImage } from "../../lib/api";

// ---------- Upload-only image picker ----------
export function ImagePicker({ value, onChange, testid = "image-picker", label = "image" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB"); return; }
    setBusy(true);
    try {
      const url = await uploadImage(file);
      onChange(url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  return (
    <div className="flex items-center gap-3">
      <div className="w-24 h-24 border border-ink overflow-hidden shrink-0 bg-secondary">
        {value ? <img src={resolveImage(value)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[10px] text-black/40">No {label}</div>}
      </div>
      <div className="flex-1 space-y-2">
        <input ref={inputRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid={`${testid}-file`} />
        <button type="button" onClick={() => inputRef.current?.click()} className="btn-primary text-[10px] px-4 py-2.5" data-testid={`${testid}-upload`}>
          {busy ? "Uploading…" : value ? "Replace image" : "Upload image"}
        </button>
        {value && <button type="button" onClick={() => onChange("")} className="ml-2 text-xs underline text-soda" data-testid={`${testid}-clear`}>Remove</button>}
        <p className="text-[10px] text-black/50">JPG / PNG / WEBP · up to 5MB · uploaded directly from your phone or computer</p>
      </div>
    </div>
  );
}

// ---------- Excel import / export ----------
export function ExcelToolbar({ onImported }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const doExport = async () => {
    try {
      const res = await api.get("/export/catalogue", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "catalogue_workbook.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Catalogue exported");
    } catch (e) { toast.error("Export failed"); }
  };

  const doImport = async (e, dryRun) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/import/catalogue?dry_run=${dryRun}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult({ ...data, dryRun });
      if (data.ok) toast.success(dryRun ? "Validation passed — ready to import" : "Import complete");
      else toast.error(`${data.errors.length} issue(s) found — see details`);
      if (!dryRun && data.ok) onImported?.();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Import failed"); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  return (
    <div className="border border-ink p-4 md:p-5 mb-8 bg-paper" data-testid="excel-toolbar">
      <div className="flex flex-wrap items-center gap-3">
        <div className="overline text-black/50 mr-2">Catalogue workbook</div>
        <button onClick={doExport} className="btn-ghost text-[11px] px-4 py-2.5" data-testid="excel-export">Export workbook (.xlsx)</button>
        <input ref={inputRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => doImport(e, false)} data-testid="excel-import-file" />
        <input type="file" accept=".xlsx" className="hidden" id="excel-validate-file" onChange={(e) => doImport(e, true)} data-testid="excel-validate-file" />
        <button onClick={() => document.getElementById("excel-validate-file").click()} className="btn-ghost text-[11px] px-4 py-2.5" disabled={busy} data-testid="excel-validate">{busy ? "Working…" : "Validate file"}</button>
        <button onClick={() => inputRef.current?.click()} className="btn-primary text-[11px] px-4 py-2.5" disabled={busy} data-testid="excel-import">Apply changes</button>
        <span className="text-[11px] text-black/50">Sheets: Instructions · Categories · Brands · Products. Match is by <b>id</b> — leave id blank to create new rows.</span>
      </div>
      {result && (
        <div className="mt-4 border-t border-black/10 pt-3 text-sm" data-testid="excel-result">
          <div className="font-bold mb-1">{result.dryRun ? "Validation preview" : "Import result"} {result.ok ? "✓" : "— issues found"}</div>
          <div className="text-xs text-black/70">
            Categories: <b className="text-cobalt">+{result.categories.created}</b> new · <b>{result.categories.updated}</b> updated&nbsp;·&nbsp;
            Brands: <b className="text-cobalt">+{result.brands.created}</b> new · <b>{result.brands.updated}</b> updated&nbsp;·&nbsp;
            Products: <b className="text-cobalt">+{result.products.created}</b> new · <b>{result.products.updated}</b> updated
          </div>
          {result.errors?.length > 0 && (
            <ul className="mt-2 text-xs text-soda list-disc pl-5 max-h-40 overflow-auto" data-testid="excel-errors">
              {result.errors.slice(0, 30).map((er, i) => <li key={i}>{er}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Inventory Excel (stock-only) ----------
export function InventoryToolbar({ onImported }) {
  const validateRef = useRef(null);
  const applyRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const doExport = async () => {
    try {
      const res = await api.get("/inventory/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "inventory_export.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Inventory exported");
    } catch (e) { toast.error("Export failed"); }
  };

  const doImport = async (e, dryRun) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/inventory/import?dry_run=${dryRun}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult({ ...data, dryRun });
      if (data.ok) {
        const parts = [];
        if (data.updated_count) parts.push(`${data.updated_count} updated`);
        if (data.created_count) parts.push(`${data.created_count} new`);
        if (data.remapped_count) parts.push(`${data.remapped_count} remapped`);
        const msg = parts.length ? parts.join(", ") : "no changes";
        toast.success(dryRun ? `Preview: ${msg}` : msg);
      } else toast.warning(`${data.updated_count} matched · ${data.unmatched_count || 0} unmatched · ${data.error_count} errors`);
      if (!dryRun && onImported) onImported();
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || "Import failed"); }
    finally { setBusy(false); if (e.target) e.target.value = ""; }
  };

  return (
    <div className="border border-ink p-4 md:p-5 mb-8 bg-[#fffcf3]" data-testid="inventory-toolbar">
      <div className="flex flex-wrap items-center gap-3">
        <div className="overline text-black/60 mr-2">Inventory Excel</div>
        <button onClick={doExport} className="btn-ghost text-[11px] px-4 py-2.5" data-testid="inv-export">Export inventory (.xlsx)</button>
        <input ref={validateRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => doImport(e, true)} data-testid="inv-validate-file" />
        <input ref={applyRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => doImport(e, false)} data-testid="inv-import-file" />
        <button onClick={() => validateRef.current?.click()} className="btn-ghost text-[11px] px-4 py-2.5" disabled={busy} data-testid="inv-validate">{busy ? "Working…" : "Validate file"}</button>
        <button onClick={() => applyRef.current?.click()} className="btn-primary text-[11px] px-4 py-2.5" disabled={busy} data-testid="inv-import">Apply stock update</button>
        <span className="text-[11px] text-black/60">Updates only <b>stock_quantity</b> + <b>availability</b>. Names, brands, prices are never touched.</span>
      </div>
      {result && (
        <div className="mt-4 border-t border-black/10 pt-3 text-sm" data-testid="inv-result">
          <div className="font-bold mb-1">
            {result.dryRun ? "Validation preview" : "Import applied"} {result.ok ? "✓" : "— issues found"}
          </div>
          <div className="text-xs text-black/70">
            {result.matched != null && <>Matched <b>{result.matched}</b> · </>}
            Updated <b>{result.updated_count || 0}</b>
            {result.created_count != null && <> · New <b>{result.created_count}</b></>}
            {result.remapped_count > 0 && <> · Remapped <b className="text-cobalt">{result.remapped_count}</b></>}
            {result.unmatched_count != null && <> · Unmatched <b>{result.unmatched_count}</b></>}
            {" · Errors "}<b>{result.error_count || 0}</b>
          </div>
          {result.updates?.length > 0 && (
            <div className="mt-2 max-h-40 overflow-auto border border-black/10">
              <table className="w-full text-xs">
                <thead className="bg-black/5"><tr><th className="text-left p-1.5">Product</th><th className="text-left p-1.5">Before</th><th className="text-left p-1.5">After</th><th className="text-left p-1.5">Availability</th></tr></thead>
                <tbody>{result.updates.slice(0, 30).map(u => (
                  <tr key={u.id} className="border-t border-black/5"><td className="p-1.5">{u.name}</td><td className="p-1.5">{u.before_stock ?? "—"}</td><td className="p-1.5 font-bold">{u.after_stock ?? "—"}</td><td className="p-1.5">{u.availability}</td></tr>
                ))}</tbody>
              </table>
            </div>
          )}
          {result.remapped?.length > 0 && (
            <div className="mt-2" data-testid="inv-remapped">
              <div className="font-bold text-xs mb-1 text-cobalt">Re-linked from Unresolved sheet</div>
              <ul className="text-xs list-disc pl-5 max-h-32 overflow-auto">
                {result.remapped.slice(0, 20).map((u, i) => (
                  <li key={i}>Batch {u.batch_id.slice(0, 8)}… → {u.product} <span className="text-black/50">(was {u.old_product_id?.slice(0, 8) || "—"}…)</span></li>
                ))}
              </ul>
            </div>
          )}
          {result.unmatched?.length > 0 && (
            <div className="mt-2 text-xs text-soda">
              <div className="font-bold mb-1">Unmatched rows (not updated)</div>
              <ul className="list-disc pl-5 max-h-24 overflow-auto">
                {result.unmatched.slice(0, 15).map((u, i) => <li key={i}>Row {u.row}: id={u.id || "—"} sku={u.sku || "—"} · {u.reason}</li>)}
              </ul>
            </div>
          )}
          {result.errors?.length > 0 && (
            <ul className="mt-2 text-xs text-soda list-disc pl-5 max-h-24 overflow-auto">
              {result.errors.slice(0, 15).map((er, i) => <li key={i}>{er}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Shared inquiry helpers ----------
export const INQUIRY_STATUSES = ["new", "contacted", "completed", "archived", "spam"];
export function StatusBadgeColor(s) {
  return s === "new" ? "text-safety border-safety"
    : s === "contacted" ? "text-cobalt border-cobalt"
    : s === "completed" ? "text-ink"
    : s === "archived" ? "text-black/50 border-black/30"
    : s === "spam" ? "text-soda border-soda" : "";
}
export function EmailDot({ sent, label }) {
  const color = sent === true ? "#0047FF" : sent === false ? "#E63946" : "#999";
  const title = sent === true ? "sent" : sent === false ? "failed" : "pending";
  return (
    <span className="inline-flex items-center gap-1 text-[10px]" title={`${label} email ${title}`}>
      <span className="inline-block w-2 h-2 rounded-full" style={{ background: color }} />
      {label} {sent === true ? "✓" : sent === false ? "✗" : "…"}
    </span>
  );
}
export function StatusSelect({ value, onChange, testid }) {
  return (
    <select value={value || "new"} onChange={e => onChange(e.target.value)} className={`field text-xs py-2 max-w-[150px] ${StatusBadgeColor(value)}`} data-testid={testid}>
      {INQUIRY_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
    </select>
  );
}

// Confirmation dialog used by Enquiry & Contact-msg delete + convert actions.
// Kept at module scope so its identity is stable and inputs never lose focus.
export function ConfirmDialog({ open, title, body, confirmLabel = "Confirm", danger = false, busy = false, onConfirm, onCancel, testid = "confirm-dialog" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-ink/70 z-50 flex items-center justify-center p-6" onClick={onCancel} data-testid={testid}>
      <div className="bg-paper border border-ink hard-shadow p-6 md:p-8 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="overline text-black/50 mb-2">{danger ? "Confirm deletion" : "Confirm"}</div>
        <h3 className="font-display text-2xl md:text-3xl uppercase leading-none mb-3">{title}</h3>
        <p className="text-sm text-black/70 whitespace-pre-line">{body}</p>
        <div className="mt-6 flex gap-3 justify-end">
          <button onClick={onCancel} className="btn-ghost text-xs" disabled={busy} data-testid={`${testid}-cancel`}>Cancel</button>
          <button onClick={onConfirm} disabled={busy}
            className={`btn-primary text-xs ${danger ? "" : ""}`}
            style={danger ? { background: "#E63946", borderColor: "#E63946" } : undefined}
            data-testid={`${testid}-confirm`}>
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal displaying the temporary password after a successful enquiry → retailer conversion.
export function ConvertResultDialog({ result, onClose }) {
  if (!result) return null;
  return (
    <div className="fixed inset-0 bg-ink/70 z-50 flex items-center justify-center p-6" onClick={onClose} data-testid="convert-result-dialog">
      <div className="bg-paper border border-ink hard-shadow p-6 md:p-8 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="overline text-cobalt mb-2">Retailer created · Pending approval</div>
        <h3 className="font-display text-2xl md:text-3xl uppercase leading-none mb-3">Share these credentials</h3>
        <p className="text-sm text-black/70">The retailer now exists in the system with <strong>pending</strong> status. Approve them from the Retailers page whenever you're ready.</p>
        <div className="mt-4 border border-ink p-4 bg-secondary space-y-2 text-sm">
          <div><span className="overline text-black/50 block">Email</span>{result.email}</div>
          <div><span className="overline text-black/50 block">Temporary password</span><code className="font-mono text-base" data-testid="convert-temp-password">{result.temporary_password}</code></div>
        </div>
        <p className="text-[11px] text-black/50 mt-3">Copy the password now — it won't be shown again. Ask the retailer to change it after their first login.</p>
        <div className="mt-6 flex gap-3 justify-end">
          <button
            onClick={() => { navigator.clipboard?.writeText(`Email: ${result.email}\nTemporary password: ${result.temporary_password}`); toast.success("Copied to clipboard"); }}
            className="btn-ghost text-xs" data-testid="convert-copy">Copy email + password</button>
          <button onClick={onClose} className="btn-primary text-xs" data-testid="convert-result-close">Done</button>
        </div>
      </div>
    </div>
  );
}

// NOTE: Field & Toggle are declared at module scope (NOT inside Settings) so their
// component identity is stable across re-renders. If they were defined inside
// Settings(), every keystroke would remount the input and steal focus.
export function Field({ label, value, onChange, type = "text", area = false, placeholder = "", testid }) {
  return (
    <div>
      <label className="overline block mb-2">{label}</label>
      {area
        ? <textarea rows={3} value={value ?? ""} onChange={e => onChange(e.target.value)} className="field" placeholder={placeholder} data-testid={testid} />
        : <input type={type} value={value ?? ""} onChange={e => onChange(e.target.value)} className="field" placeholder={placeholder} data-testid={testid} />}
    </div>
  );
}

export function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center justify-between gap-4 border border-ink p-4 cursor-pointer" data-testid={testid}>
      <span className="text-sm font-medium">{label}</span>
      <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} className="w-5 h-5" />
    </label>
  );
}
